import type { StrokeCycle } from './types';

/**
 * Calculates effective minimum time for initial view
 * Uses first stroke start if available, otherwise uses timeExtent[0]
 */
export function calculateEffectiveTimeMin(
  firstStrokeStartTime: number | null,
  timeExtent: [number, number]
): number {
  return firstStrokeStartTime != null && firstStrokeStartTime > timeExtent[0]
    ? firstStrokeStartTime
    : timeExtent[0];
}

/**
 * Calculates window bounds (start and end) based on current state
 */
export function calculateWindowBounds(params: {
  perStrokeMode: boolean;
  isInitialView: boolean;
  selectedStrokeIndex: number | null;
  allStrokeCycles: StrokeCycle[];
  currentTime: number;
  timeExtent: [number, number];
  effectiveTimeMin: number;
  windowDuration: number;
}): { windowStart: number; windowEnd: number } {
  const {
    perStrokeMode,
    isInitialView,
    selectedStrokeIndex,
    allStrokeCycles,
    currentTime,
    timeExtent,
    effectiveTimeMin,
    windowDuration,
  } = params;

  const minTime = isInitialView ? effectiveTimeMin : timeExtent[0];

  // Per Stroke Mode: Show single stroke view
  // Normal Mode: Show ~7–8 strokes (8-second window)
  if (
    perStrokeMode &&
    (isInitialView || selectedStrokeIndex !== null) &&
    allStrokeCycles.length > 0
  ) {
    // Per Stroke Mode ON: Show single stroke
    const strokeIndex = selectedStrokeIndex !== null ? selectedStrokeIndex : 0;
    const stroke = allStrokeCycles[strokeIndex];
    if (stroke) {
      // Show selected stroke with padding (10% on each side) for better visualization
      const strokeDuration = stroke.end - stroke.start;
      const padding = strokeDuration * 0.1;
      return {
        windowStart: Math.max(timeExtent[0], stroke.start - padding),
        windowEnd: Math.min(timeExtent[1], stroke.end + padding),
      };
    } else {
      // Fallback to normal behavior if stroke index is invalid
      return {
        windowStart: currentTime - windowDuration / 2,
        windowEnd: currentTime + windowDuration / 2,
      };
    }
  } else if (currentTime - windowDuration / 2 < minTime) {
    // Clamped at start: window moves with currentTime, maintaining full width
    const windowStart = Math.max(minTime, currentTime);
    return {
      windowStart,
      windowEnd: Math.min(timeExtent[1], windowStart + windowDuration),
    };
  } else if (currentTime + windowDuration / 2 > timeExtent[1]) {
    // Clamped at end: show full window to end
    return {
      windowEnd: timeExtent[1],
      windowStart: Math.max(minTime, timeExtent[1] - windowDuration),
    };
  } else {
    // Not clamped: center around currentTime
    return {
      windowStart: currentTime - windowDuration / 2,
      windowEnd: currentTime + windowDuration / 2,
    };
  }
}

/**
 * Calculates window range label string
 */
export function calculateWindowRangeLabel(
  hasData: boolean,
  windowStart: number,
  windowEnd: number
): string {
  if (!hasData) return '';
  return `${Math.round(windowStart)}s - ${Math.round(windowEnd)}s`;
}
